import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Portal } from 'react-portal';
import { Text } from 'rebass';
import Accordion from '@/components/Accordion/Accordion';
import AccordionItem from '@/components/Accordion/AccordionItem';
import Button from '@/components/Button/Button';
import Switch from '@/components/Switch';
import useNotify from '@/hooks/useNotify';
import type { MfaMethod } from '@ecosconnect/core/src/auth/types';
import CodeVerificationModal, {
  VerificationFlow,
} from './CodeVerificationModal';
import PasskeysModal from './PasskeysModal';
import RowValue from './RowValue';
import { SECURITY_SETTINGS, SET_TWO_FACTOR_METHOD } from './Security.queries';
import TwoFactorMethodPicker from './TwoFactorMethodPicker';

type Open = { modal: 'verify'; flow: VerificationFlow } | { modal: 'passkeys' };

const passkeyCount = (count: number) =>
  count ? `${count} ${count === 1 ? 'passkey' : 'passkeys'}` : 'None';

const SecuritySection: React.FC = () => {
  const { showError } = useNotify();
  const [open, setOpen] = useState<Open>();
  const close = () => setOpen(undefined);

  const { data, loading, error, refetch } = useQuery(SECURITY_SETTINGS, {
    fetchPolicy: 'network-only',
  });
  const [pendingMethod, setPendingMethod] = useState<MfaMethod>();
  const [setTwoFactorMethod] = useMutation(SET_TWO_FACTOR_METHOD, {
    refetchQueries: [SECURITY_SETTINGS],
    awaitRefetchQueries: true,
    onError: (err) => showError(err.message),
  });
  const security = data?.securitySettings;

  const changeMethod = async (value: MfaMethod) => {
    if (value === 'email' && !security?.emailVerified) {
      setOpen({ modal: 'verify', flow: 'useEmailForTwoFactor' });
      return;
    }
    setPendingMethod(value);
    await setTwoFactorMethod({ variables: { method: value } });
    setPendingMethod(undefined);
  };

  return (
    <>
      <Accordion title="Security" variant="onMain">
        {loading && (
          <Text variant="body2" color="fieldBorderHover">
            Loading security settings…
          </Text>
        )}
        {error && (
          <Text variant="body2" color="statusError" role="alert">
            {error.message}
          </Text>
        )}
        {security && (
          <>
            <AccordionItem
              label="Two-factor authentication"
              value={
                security.twoFactorEnabled || security.hasPassword ? (
                  <RowValue
                    text={security.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  >
                    <Switch
                      label="Two-factor authentication"
                      checked={security.twoFactorEnabled}
                      onCheckedChange={(checked) =>
                        setOpen({
                          modal: 'verify',
                          flow: checked
                            ? 'turnOnTwoFactor'
                            : 'turnOffTwoFactor',
                        })
                      }
                    />
                  </RowValue>
                ) : (
                  <span>Requires a password on your account</span>
                )
              }
            />
            {security.twoFactorMethod && (
              <AccordionItem
                label="Send codes by"
                value={
                  <TwoFactorMethodPicker
                    value={pendingMethod ?? security.twoFactorMethod}
                    hasPhone={!!security.phone}
                    disabled={!!pendingMethod}
                    align="flex-end"
                    onChange={changeMethod}
                  />
                }
              />
            )}
            <AccordionItem
              label="Google account"
              value={
                <span>{security.googleAccount?.email ?? 'Not linked'}</span>
              }
            />
            <AccordionItem
              label="Passkeys"
              value={
                <RowValue text={passkeyCount(security.passkeys.length)}>
                  <Button
                    variant="outline"
                    onClick={() => setOpen({ modal: 'passkeys' })}
                  >
                    Manage passkeys
                  </Button>
                </RowValue>
              }
            />
          </>
        )}
      </Accordion>
      <Portal>
        {open?.modal === 'verify' && (
          <CodeVerificationModal flow={open.flow} onClose={close} />
        )}
        {open?.modal === 'passkeys' && security && (
          <PasskeysModal
            passkeys={security.passkeys}
            hasPassword={security.hasPassword}
            onAdded={refetch}
            onClose={close}
          />
        )}
      </Portal>
    </>
  );
};

export default SecuritySection;
